/**
 * Shared filter state for the analytics section.
 *
 * The analytics views (Overview / Lives / Medias) all live under the
 * same parent shell which renders ONE toolbar — date range applies to
 * every tab; live + show selectors only show on the Lives tab; media
 * + format selectors only show on the Medias tab. We park the state
 * here so:
 *
 *   1. Switching tabs preserves the date range — going from Lives to
 *      Overview shouldn't reset the user's last 30d window.
 *   2. The toolbar (in the parent) and the body (in the child) read
 *      from the same source of truth without prop-drilling.
 *   3. Staged-vs-applied separation is explicit: the toolbar mutates
 *      the `pending.*` slots; clicking Search promotes them to the
 *      `applied.*` slots; child views watch only `applied.*` so they
 *      reload on commit, not on every keystroke.
 *
 * The committed slots default to "Last 7 Days" — same window the old
 * monolithic analytics page used.
 */

/** Local midnight today. Default for the "Today" preset. */
const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/* Ads-view panel state — persisted per-user via localStorage so the
 * choice survives reloads. Not analytics state (doesn't gate any
 * fetch), just display preference. */
const LS_ADS_PANEL     = 'lamusica_platform_analytics_ads_panel'          // 'off' | 'revenue' | 'opportunities'
const LS_REV_CPM       = 'lamusica_platform_analytics_revenue_cpm'
// Legacy per-pipeline keys — read once on first load so users who set
// a CPM under the old two-input UI don't lose their value. The read
// side prefers LS_REV_CPM, then falls back to whichever legacy key is
// non-zero (IMA first, SSAI as tiebreaker). Writes only ever hit the
// new unified key.
const LS_REV_CPM_IMA_LEGACY  = 'lamusica_platform_analytics_revenue_cpm_ima'
const LS_REV_CPM_SSAI_LEGACY = 'lamusica_platform_analytics_revenue_cpm_ssai'
const LS_OPP_DUR_SEC   = 'lamusica_platform_analytics_opportunities_ad_dur_sec'
const LS_OPP_CPM       = 'lamusica_platform_analytics_opportunities_cpm'
// Historical key we still read for backward-compat (before we split
// the panel state out of revenue). Migrates the old boolean into the
// new tri-state on first load; the write side always uses LS_ADS_PANEL.
const LS_REV_ENABLED_LEGACY = 'lamusica_platform_analytics_revenue_enabled'

const readString = (k, fallback) => {
  if (typeof localStorage === 'undefined') return fallback
  const v = localStorage.getItem(k)
  return v == null ? fallback : v
}
const readNumber = (k, fallback) => {
  if (typeof localStorage === 'undefined') return fallback
  const v = Number(localStorage.getItem(k))
  return Number.isFinite(v) && v >= 0 ? v : fallback
}

/** Resolve the initial ads-panel state, honoring the legacy boolean. */
const readAdsPanel = () => {
  const explicit = readString(LS_ADS_PANEL, null)
  if (explicit === 'off' || explicit === 'revenue' || explicit === 'opportunities') return explicit
  // Migrate: old boolean → 'revenue' when true, 'off' when false/absent.
  if (typeof localStorage === 'undefined') return 'off'
  try {
    const legacy = JSON.parse(localStorage.getItem(LS_REV_ENABLED_LEGACY))
    return legacy === true ? 'revenue' : 'off'
  } catch (_) { return 'off' }
}

const initialState = () => ({
  // Currently-applied filters — drives the analytics queries the
  // child views fire. Only mutated by APPLY (Search button) so the
  // children don't refetch on every keystroke.
  applied: {
    dateFrom: startOfToday(),
    dateTo:   new Date(),
    presetLabel: 'Today',
    liveId:   null,
    showId:   null,
    mediaId:  null,
    format:   null,
    // Lives-tab cross-section: 'LaMusicaWeb' | 'LaMusicaAndroid' |
    // 'LaMusicaIOS' | 'LaMusicaTV' | null (all). Sent to the API as
    // `?appName=...` — matches the SDK's `app_name` event payload.
    appName: null,
    // Category ids (Overview / Lives / Medias). Sent as
    // `?categoryId=a,b`; the API expands each to its lives / medias,
    // subcategories included.
    categoryIds: [],
    // Ads-tab only: pipeline filter — 'ima' | 'ssai' | null (All).
    // Maps to the server's `?pipeline=...` query param so every ad
    // metric in the response reflects only the selected pipeline.
    adType: null,
    // Ads-tab only: which entity dimension the ads are sliced by.
    // 'live' shows the live + show selectors, 'media' shows the
    // media + format selectors. Default 'live' since live streams
    // are the primary ad-bearing surface.
    targetType: 'live'
  },
  // In-flight (staged) filters — what the toolbar has set but the
  // user hasn't committed yet. Search promotes these into `applied`.
  pending: {
    dateFrom: startOfToday(),
    dateTo:   new Date(),
    presetLabel: 'Today',
    liveId:   null,
    showId:   null,
    mediaId:  null,
    format:   null,
    appName: null,
    categoryIds: [],
    adType: null,
    targetType: 'live'
  },
  // True when pending differs from applied — drives the Search
  // button's enabled state and the "unsaved changes" hint.
  dirty: false,

  // Ads-view panel selector — which secondary section shows under the
  // main KPI strip on the /analytics/ads route. 'off' hides both.
  // Not a filter; doesn't gate any API call. Persisted per-user.
  adsPanel: readAdsPanel(),

  // Revenue calculator config (Ads view only, when adsPanel === 'revenue').
  // Single CPM applied to whichever pipeline(s) the ad-type filter
  // includes — the chart draws separate series per pipeline so one
  // rate powers both when "All" is selected. Reads the unified key
  // first, then either legacy per-pipeline key so pre-existing values
  // aren't wiped on first load after the unify.
  revenue: {
    cpm: readNumber(LS_REV_CPM, 0)
      || readNumber(LS_REV_CPM_IMA_LEGACY, 0)
      || readNumber(LS_REV_CPM_SSAI_LEGACY, 0)
  },

  // Opportunities config (Ads view only, when adsPanel === 'opportunities').
  //   adDurationSec — fallback used when ads.avgDurationMs is null or
  //                   zero; expected-ad-count math needs SOMETHING to
  //                   divide by. 30 s is the industry-standard spot.
  //   cpm           — dollar rate applied to the expected-ad count to
  //                   surface potential revenue. Independent of the
  //                   Revenue panel's `revenue.cpm` because the two
  //                   panels ask different questions (Revenue models
  //                   actual impressions; Opportunities models the
  //                   inventory ceiling given break time).
  opportunities: {
    adDurationSec: readNumber(LS_OPP_DUR_SEC, 30) || 30,
    cpm:           readNumber(LS_OPP_CPM, 0)
  }
})

const analytics = {
  namespaced: true,

  state: initialState,

  getters: {
    applied:  state => state.applied,
    pending:  state => state.pending,
    dirty:    state => state.dirty,

    // Sugar for the most-read fields — child views can `mapGetters`
    // these without reaching into the nested `applied` object.
    dateFrom: state => state.applied.dateFrom,
    dateTo:   state => state.applied.dateTo,
    liveId:   state => state.applied.liveId,
    showId:   state => state.applied.showId,
    mediaId:  state => state.applied.mediaId,
    format:     state => state.applied.format,
    appName:    state => state.applied.appName,
    categoryIds: state => state.applied.categoryIds,
    adType:        state => state.applied.adType,
    targetType:    state => state.applied.targetType,
    adsPanel:      state => state.adsPanel,
    revenue:       state => state.revenue,
    opportunities: state => state.opportunities
  },

  mutations: {
    SET_PENDING(state, partial) {
      state.pending = { ...state.pending, ...partial }
      state.dirty = JSON.stringify(state.pending) !== JSON.stringify(state.applied)
    },
    APPLY(state) {
      state.applied = { ...state.pending }
      state.dirty = false
    },
    RESET_PENDING(state) {
      state.pending = { ...state.applied }
      state.dirty = false
    },
    // Full reset back to initialState — used when the user leaves the
    // /analytics section entirely so a later return isn't stuck on a
    // stale live/media/show filter picked in the previous session.
    // ads-panel + revenue + opportunities are cosmetic prefs persisted
    // in localStorage and stay unchanged.
    RESET_ALL(state) {
      const fresh = initialState()
      state.applied = fresh.applied
      state.pending = fresh.pending
      state.dirty = false
    },
    // Used by deep-links (e.g. /analytics/lives?id=X) that need to
    // seed both pending and applied from a URL query param so the
    // first page load doesn't require a Search click.
    SEED(state, partial) {
      state.applied = { ...state.applied, ...partial }
      state.pending = { ...state.applied }
      state.dirty = false
    },

    // Ads-panel selector. Off / Revenue / Opportunities is a single
    // shared field so switching between them is one dispatch, not two
    // separate booleans that could drift out of sync.
    SET_ADS_PANEL(state, panel) {
      const next = (panel === 'revenue' || panel === 'opportunities') ? panel : 'off'
      state.adsPanel = next
      if (typeof localStorage !== 'undefined') {
        try { localStorage.setItem(LS_ADS_PANEL, next) } catch (_) {}
      }
    },

    // Revenue calculator config. Persists each change to localStorage
    // so the next page load restores the user's last CPM without
    // round-tripping through the API.
    SET_REVENUE(state, partial) {
      state.revenue = { ...state.revenue, ...partial }
      if (typeof localStorage !== 'undefined') {
        try {
          if ('cpm' in partial) localStorage.setItem(LS_REV_CPM, String(state.revenue.cpm))
        } catch (_) { /* private mode, sandboxed iframe — silent */ }
      }
    },

    // Opportunities config. Same persistence contract as revenue.
    SET_OPPORTUNITIES(state, partial) {
      state.opportunities = { ...state.opportunities, ...partial }
      if (typeof localStorage !== 'undefined') {
        try {
          if ('adDurationSec' in partial) localStorage.setItem(LS_OPP_DUR_SEC, String(state.opportunities.adDurationSec))
          if ('cpm'           in partial) localStorage.setItem(LS_OPP_CPM,     String(state.opportunities.cpm))
        } catch (_) {}
      }
    }
  },

  actions: {
    setPending({ commit }, partial) { commit('SET_PENDING', partial) },
    apply({ commit }) { commit('APPLY') },
    resetPending({ commit }) { commit('RESET_PENDING') },
    resetAll({ commit }) { commit('RESET_ALL') },
    seed({ commit }, partial) { commit('SEED', partial) },
    setAdsPanel({ commit }, panel) { commit('SET_ADS_PANEL', panel) },
    setRevenue({ commit }, partial) { commit('SET_REVENUE', partial) },
    setOpportunities({ commit }, partial) { commit('SET_OPPORTUNITIES', partial) }
  }
}

export default analytics
